import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getAdminOfferPromos, deleteOfferPromo } from '../../api/adminApi';
import { offerImageUrl } from '../../api/offerPromoApi';
import { fmtIST } from '../../utils/dateFormat';

const cyan = '#00e5ff';
const LIMIT = 10;

const statusColors = { active: '#66ff99', inactive: '#888', expired: '#ff6666' };

const btn = (color) => ({
  background: `${color}1a`,
  border: `1px solid ${color}4d`,
  color,
  borderRadius: 6,
  padding: '4px 10px',
  fontSize: 11,
  fontWeight: 600,
  cursor: 'pointer',
});

const AdminOfferPromos = () => {
  const navigate = useNavigate();
  const [offers, setOffers] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getAdminOfferPromos({ page, limit: LIMIT })
      .then((res) => {
        setOffers(res.data.offers);
        setTotal(res.data.total);
        setPages(res.data.pages);
      })
      .catch(() => toast.error('Failed to load offers'))
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async (offer) => {
    if (!window.confirm(`Delete offer "${offer.title}"?`)) return;
    try {
      await deleteOfferPromo(offer._id);
      toast.success('Offer deleted');
      if (offers.length === 1 && page > 1) setPage(page - 1);
      else load();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 12, flexWrap: 'wrap' }}>
        <div>
          <h4 style={{ color: '#fff', fontWeight: 700, marginBottom: 4 }}>Offers Promo</h4>
          <div style={{ color: '#555', fontSize: 13 }}>{total} total offers</div>
        </div>
        <button
          onClick={() => navigate('/panel/offer-promos/new')}
          style={{ background: cyan, border: 'none', color: '#000', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}
        >
          + Add Offer
        </button>
      </div>

      <div style={{ background: '#111', border: '1px solid rgba(0,229,255,0.1)', borderRadius: 12, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: 'rgba(0,229,255,0.07)', borderBottom: '1px solid rgba(0,229,255,0.12)' }}>
              {['Image', 'Title', 'Description', 'Status', 'Expiry Date', 'Actions'].map((h) => (
                <th key={h} style={{ color: cyan, fontSize: 12, fontWeight: 600, padding: '12px 14px', textAlign: 'left', whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>Loading...</td>
              </tr>
            ) : offers.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ color: '#555', textAlign: 'center', padding: 32, fontSize: 13 }}>No offers found</td>
              </tr>
            ) : (
              offers.map((o) => {
                const status = new Date(o.expiryDate) < new Date() ? 'expired' : o.status;
                return (
                  <tr key={o._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                    <td style={{ padding: '10px 14px' }}>
                      <img src={offerImageUrl(o.image)} alt={o.title} style={{ width: 72, height: 44, objectFit: 'cover', borderRadius: 6, display: 'block' }} />
                    </td>
                    <td style={{ color: '#fff', fontSize: 13, fontWeight: 600, padding: '10px 14px', maxWidth: 180 }}>{o.title}</td>
                    <td style={{ color: '#888', fontSize: 12, padding: '10px 14px', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {o.description || '—'}
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: `${statusColors[status]}20`, color: statusColors[status], border: `1px solid ${statusColors[status]}40`, borderRadius: 6, padding: '2px 8px', fontSize: 10, fontWeight: 600, textTransform: 'capitalize' }}>
                        {status}
                      </span>
                    </td>
                    <td style={{ color: '#aaa', fontSize: 12, padding: '10px 14px', whiteSpace: 'nowrap' }}>{fmtIST(o.expiryDate)}</td>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => navigate(`/panel/offer-promos/${o._id}/edit`)} style={{ ...btn(cyan), marginRight: 8 }}>Edit</button>
                      <button onClick={() => handleDelete(o)} style={btn('#ff6666')}>Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
          {Array.from({ length: pages }, (_, i) => i + 1).map((p) => (
            <button
              key={p}
              onClick={() => setPage(p)}
              style={{
                background: p === page ? cyan : 'rgba(255,255,255,0.06)',
                border: 'none',
                color: p === page ? '#000' : '#888',
                borderRadius: 6,
                padding: '6px 12px',
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {p}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminOfferPromos;
